import { createContext, useContext, useState } from "react";

import { useLocalStorage } from "./use-local-storage";
import { getTempScribenoteFlask } from "../utils";

const TwilioAPIContext = createContext();

export const useTwilioAPI = () => {
  const context = useContext(TwilioAPIContext);
  if (!context) {
    throw new Error(
      "useTwilioAPI must be used within a TwilioAPIProvider",
    );
  }
  return context;
};

export const TwilioAPIProvider = ({ children }) => {
  // Use localStorage for persistence over refresh
  const [storedRecordingMode, setStoredRecordingMode] =
    useLocalStorage("call-recording-mode", false);
  const [isRecordingScribephoneMode, setIsRecordingScribephoneMode] =
    useState(storedRecordingMode);

  const updateRecordingMode = (isScribephone) => {
    setIsRecordingScribephoneMode(isScribephone);
    setStoredRecordingMode(isScribephone);
  };

  const _request = async (endpoint, body) => {
    const SCRIBENOTE_FLASK = getTempScribenoteFlask();
    const url = `${SCRIBENOTE_FLASK}/api/twilio/v1/${endpoint}`;
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const error =
        errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(error);
    }

    return await response.json();
  };

  const initiateCall = async (
    phoneNumber,
    callMetadata = {},
    countryCode = "US",
    fromNumber,
  ) => {
    return _request("initiate-call", {
      phone_number: phoneNumber,
      call_metadata: callMetadata,
      country_code: countryCode,
      ...(fromNumber ? { from_number: fromNumber } : {}),
    });
  };

  const endCall = async (callSid) => {
    return _request("end-call", { call_sid: callSid });
  };

  const getConferenceStatus = async (conferenceName) => {
    const SCRIBENOTE_FLASK = getTempScribenoteFlask();
    const response = await fetch(
      `${SCRIBENOTE_FLASK}/api/twilio/v1/conference-status/${conferenceName}`,
      {
        method: "GET",
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      const error =
        errorData.error || `HTTP error! status: ${response.status}`;
      throw new Error(error);
    }

    return await response.json();
  };

  const value = {
    isRecordingScribephoneMode,
    updateRecordingMode,
    initiateCall,
    endCall,
    getConferenceStatus,
  };

  return (
    <TwilioAPIContext.Provider value={value}>
      {children}
    </TwilioAPIContext.Provider>
  );
};
